import React, { useCallback, useEffect, useState } from 'react';
import { useNotify, usePermissions, useRecordContext } from 'react-admin';
import { gql } from 'apollo-boost';
import {
    Box, Button, Card, CardContent, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import moment from 'moment';
import { client } from '../helpers/apolloClient';

// The standing allowance lives on the beneficiary, but is deliberately not on
// the ordinary Record type: reading or writing it there would expose what a
// beneficiary is paid to every role, and let an admin change it. Both calls
// below are finance-guarded, so this panel may only ever mount for finance.
const PROFILE_QUERY = gql`
    query paymentProfile($id: MongoID!) {
        paymentProfile(id: $id) {
            id
            monthlyAllowance
            allowanceHistory { amount previousAmount note changedBy changedAt }
        }
    }
`;

const SET_ALLOWANCE = gql`
    mutation setMonthlyAllowance($record: SetMonthlyAllowanceInput!) {
        setMonthlyAllowance(record: $record) { id monthlyAllowance }
    }
`;

const money = value => (typeof value === 'number' ? `${value} ETB` : '—');

// The payments panel for one beneficiary: the standing monthly allowance and
// every change ever made to it. The id comes from the Show record context, not
// props - <Resource> renders the show component with no props.
export const BeneficiaryPayments = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const { permissions } = usePermissions();
    const isFinance = !!permissions
        && (permissions.includes('finance') || permissions.includes('financeAdmin'));

    const id = record?.id;

    const [profile, setProfile] = useState(null);
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        if (!id || !isFinance) return;
        client.query({ query: PROFILE_QUERY, variables: { id }, fetchPolicy: 'network-only' })
            .then(({ data }) => {
                setProfile(data.paymentProfile);
                setAmount(
                    typeof data.paymentProfile.monthlyAllowance === 'number'
                        ? String(data.paymentProfile.monthlyAllowance)
                        : ''
                );
            })
            .catch(() => notify('Could not load the payment details', { type: 'warning' }));
    }, [id, isFinance, notify]);

    useEffect(load, [load]);

    if (!id || !isFinance) return null;

    const save = () => {
        let value = null;
        if (String(amount).trim() !== '') {
            value = Number(amount);
            if (!Number.isInteger(value) || value < 0) {
                notify('The monthly allowance must be a whole, non-negative number of birr.', { type: 'warning' });
                return;
            }
        }

        setSaving(true);
        client.mutate({
            mutation: SET_ALLOWANCE,
            variables: { record: { id, amount: value, note: note.trim() || undefined } },
        })
            .then(() => {
                notify('Monthly allowance saved');
                setNote('');
                load();
            })
            .catch(e => notify(e.message, { type: 'warning' }))
            .finally(() => setSaving(false));
    };

    // Stored oldest-first so the record reads as an account of what happened;
    // shown newest-first because that is the entry anyone cares about.
    const history = profile ? [...profile.allowanceHistory].reverse() : [];

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Monthly allowance
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    The standing amount paid every month. Later months snapshot it; changing it here never changes a month already generated.
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ my: 2, flexWrap: 'wrap' }}>
                    <TextField
                        size="small" type="number" label="Amount (ETB)"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        helperText="Leave empty to clear"
                        sx={{ maxWidth: 200 }}
                    />
                    <TextField
                        size="small" label="Reason (optional)"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        sx={{ minWidth: 280 }}
                    />
                    <Button variant="contained" size="small" disabled={saving} onClick={save}>
                        Save
                    </Button>
                </Stack>

                {profile && profile.monthlyAllowance === null && history.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No allowance has been set yet.
                    </Typography>
                )}

                {history.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>History</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>When</TableCell>
                                    <TableCell>Change</TableCell>
                                    <TableCell>Reason</TableCell>
                                    <TableCell>By</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map((entry, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {entry.changedAt ? moment(Number(entry.changedAt)).format('YYYY-MM-DD HH:mm') : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {money(entry.previousAmount)} &rarr; {money(entry.amount)}
                                        </TableCell>
                                        <TableCell>{entry.note || '—'}</TableCell>
                                        <TableCell>{entry.changedBy || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default BeneficiaryPayments;
